import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useSystemSettings } from '@/hooks/useSystemSettings';

export type SubscriptionAccessLevel = 
  | 'loading'
  | 'no_subscription'  // New user, no subscription yet
  | 'pending_payment'   // Has pending checkout but payment not completed
  | 'free'              // Active free tier subscription
  | 'paid'              // Active paid subscription (standard plan)
  | 'custom'            // Active custom plan subscription
  | 'expired'           // Subscription has expired
  | 'subscriptions_disabled'; // Subscriptions feature is turned off

interface SubscriptionAccessInfo {
  isLoading: boolean;
  accessLevel: SubscriptionAccessLevel;
  hasValidPaidSubscription: boolean;
  isCustomPlan: boolean;
  featureKeys: string[];
  planType: string | null;
  requiresPayment: boolean;
}

interface SubscriptionData {
  id: string;
  status: string;
  is_custom_plan: boolean;
  custom_features: { feature_keys?: string[]; enabled_features?: string[] } | null;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  current_period_end: string | null;
  is_free_assignment: boolean;
  is_admin_assigned: boolean;
  subscription_plans: {
    plan_type: string;
    name: string;
  } | null;
}

async function fetchSubscriptionAccess(): Promise<{
  accessLevel: SubscriptionAccessLevel;
  isCustomPlan: boolean;
  featureKeys: string[];
  planType: string | null;
  hasStripeSubscription: boolean;
}> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) {
    return {
      accessLevel: 'no_subscription',
      isCustomPlan: false,
      featureKeys: [],
      planType: null,
      hasStripeSubscription: false,
    };
  }

  const { data: subscription, error } = await supabase
    .from('user_subscriptions')
    .select(`
      id,
      status,
      is_custom_plan,
      custom_features,
      stripe_subscription_id,
      stripe_customer_id,
      current_period_end,
      is_free_assignment,
      is_admin_assigned,
      subscription_plans!user_subscriptions_plan_id_fkey(plan_type, name)
    `)
    .eq('user_id', userData.user.id)
    .eq('status', 'active')
    .maybeSingle() as { data: SubscriptionData | null; error: any };

  if (error) {
    console.error('Error fetching subscription access:', error);
  }

  // No active subscription at all
  if (!subscription) {
    return {
      accessLevel: 'no_subscription',
      isCustomPlan: false,
      featureKeys: [],
      planType: null,
      hasStripeSubscription: false,
    };
  }

  const planType = subscription.subscription_plans?.plan_type || null;
  const hasStripeSubscription = !!subscription.stripe_subscription_id;
  
  // Check for custom plan
  if (subscription.is_custom_plan) {
    const customFeatures = subscription.custom_features;
    const featureKeys = customFeatures?.enabled_features || customFeatures?.feature_keys || [];
    
    // Custom plan with Stripe subscription = fully paid
    // Custom plan without Stripe but admin-assigned with free_assignment = valid free custom
    // Custom plan without Stripe and not admin-assigned = pending payment
    if (hasStripeSubscription) {
      return {
        accessLevel: 'custom',
        isCustomPlan: true,
        featureKeys,
        planType: 'custom',
        hasStripeSubscription: true,
      };
    }
    
    // Admin-assigned custom plans (with or without payment)
    if (subscription.is_admin_assigned) {
      return {
        accessLevel: 'custom',
        isCustomPlan: true,
        featureKeys,
        planType: 'custom',
        hasStripeSubscription: false,
      };
    }
    
    // Custom plan created but no Stripe subscription and not admin-assigned = pending payment
    return {
      accessLevel: 'pending_payment',
      isCustomPlan: true,
      featureKeys,
      planType: 'custom',
      hasStripeSubscription: false,
    };
  }

  // Standard subscription plans
  if (planType === 'free') {
    return {
      accessLevel: 'free',
      isCustomPlan: false,
      featureKeys: [],
      planType: 'free',
      hasStripeSubscription,
    };
  }

  // Paid standard plans - verify they have Stripe subscription or are admin-assigned
  if (['basic', 'premium', 'enterprise'].includes(planType || '')) {
    if (hasStripeSubscription || subscription.is_admin_assigned) {
      return {
        accessLevel: 'paid',
        isCustomPlan: false,
        featureKeys: [],
        planType,
        hasStripeSubscription,
      };
    }
    
    // Paid plan type but no payment verification
    return {
      accessLevel: 'pending_payment',
      isCustomPlan: false,
      featureKeys: [],
      planType,
      hasStripeSubscription: false,
    };
  }

  // Default to no subscription for unknown states
  return {
    accessLevel: 'no_subscription',
    isCustomPlan: false,
    featureKeys: [],
    planType: null,
    hasStripeSubscription: false,
  };
}

export function useSubscriptionAccess(): SubscriptionAccessInfo {
  const { isAuthenticated } = useAuth();
  const { getPublicBooleanSetting } = useSystemSettings();

  const { data: subscriptionsEnabled, isLoading: isLoadingSettings } = useQuery({
    queryKey: ['subscriptions-enabled-setting'],
    queryFn: async () => getPublicBooleanSetting('subscriptions_enabled', true),
    staleTime: 1000 * 60 * 5,
  });

  const subscriptionQueryEnabled = isAuthenticated && subscriptionsEnabled !== false;

  const { data, isLoading: isLoadingSubscription, fetchStatus } = useQuery({
    queryKey: ['subscription-access'],
    queryFn: fetchSubscriptionAccess,
    enabled: subscriptionQueryEnabled,
    staleTime: 1000 * 30, // 30 seconds - shorter for more responsive updates
    refetchOnWindowFocus: true,
    refetchOnMount: 'always', // Always refetch on mount to get latest status
  });

  // Consider loading if: settings loading, subscription query loading,
  // OR the query should be enabled but hasn't returned data yet (race condition fix)
  const isQueryPendingWithoutData = subscriptionQueryEnabled && !data && fetchStatus === 'idle';
  const isLoading = isLoadingSettings || isLoadingSubscription || isQueryPendingWithoutData;

  return useMemo(() => {
    if (isLoading) {
      return {
        isLoading: true,
        accessLevel: 'loading' as const,
        hasValidPaidSubscription: false,
        isCustomPlan: false,
        featureKeys: [],
        planType: null,
        requiresPayment: false,
      };
    }

    // If subscriptions are disabled, grant full access
    if (subscriptionsEnabled === false) {
      return {
        isLoading: false,
        accessLevel: 'subscriptions_disabled' as const,
        hasValidPaidSubscription: true,
        isCustomPlan: false,
        featureKeys: [],
        planType: null,
        requiresPayment: false,
      };
    }

    if (!data) {
      return {
        isLoading: false,
        accessLevel: 'no_subscription' as const,
        hasValidPaidSubscription: false,
        isCustomPlan: false,
        featureKeys: [],
        planType: null,
        requiresPayment: true,
      };
    }

    const hasValidPaidSubscription = 
      data.accessLevel === 'paid' || 
      data.accessLevel === 'custom';
    
    const requiresPayment = 
      data.accessLevel === 'no_subscription' || 
      data.accessLevel === 'pending_payment' ||
      data.accessLevel === 'expired';

    return {
      isLoading: false,
      accessLevel: data.accessLevel,
      hasValidPaidSubscription,
      isCustomPlan: data.isCustomPlan,
      featureKeys: data.featureKeys,
      planType: data.planType,
      requiresPayment,
    };
  }, [isLoading, subscriptionsEnabled, data]);
}
